import React, { Component } from 'react';
import FaqSection from './FaqSection';
import {
  SearchFilled,
  DocumentFilled,
  FolderFilled,
  SettingsFilled,
  ImageFilled,
  MailFilled,
  ChatFilled,
  PeopleFilled,
} from '@fluentui/react-icons';
import './pentities.css';
import './docSearch.css';
import msAppSource from './assets/MS_AppSource.webp';
import NavBar from './NavBar';

interface DocSearchPageProps {}

interface DocSearchPageState {}

class DocSearchPage extends Component<DocSearchPageProps, DocSearchPageState> {
  render() {
    return (
      <div className="pentities-page docsearch-v2-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="pentities-hero-section">
          <div className="pentities-hero-video-background">
            <video preload="metadata" autoPlay loop muted playsInline className="pentities-hero-video">
              <source src={require('./assets/video/web/14477265_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="pentities-container">
            <div className="pentities-hero-content">
              <span className="pentities-hero-eyebrow">SharePoint Search &amp; Content</span>
              <h1 className="pentities-hero-title">
                Search, preview and manage every file across Microsoft 365 from one app
              </h1>
              <p className="pentities-hero-description">
                DocSearch unifies search and content management across OneDrive, SharePoint and Teams. Find files,
                list items, sites, images, emails and chats, preview them in place, and manage sharing, permissions
                and metadata all without leaving a single interface.
              </p>
              <div className="pentities-hero-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pentities-hero-cta-btn"
                >
                  Request Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description */}
        <section id="overview" className="pentities-gendesc-section">
          <div className="pentities-container">
            <div className="pentities-gendesc-content">
              <span className="pentities-gendesc-label">What is DocSearch?</span>
              <h2 className="pentities-gendesc-title">One search, every content source, zero context-switching</h2>
              <p>
                DocSearch is a robust SharePoint application that optimizes search and content management across
                OneDrive, SharePoint and Teams. It streamlines file management and lets you work with SharePoint
                list records from any site or Team all from a single place.
              </p>
              <p>
                <b>Consolidated content.</b> Define your primary lists and libraries for direct access, then search
                across every environment you can reach. Results appear in one clean interface where you can view
                content and manage sharing and permissions seamlessly.
              </p>
              <p>
                <b>Continuous preview.</b> Documents, images, list items and emails are always previewable in
                place, so you can review the content you need without ever navigating away from your results.
              </p>
              <p>
                <b>Time back in your day.</b> Centralising retrieval, management, sharing and collaboration in one
                app removes the constant hopping between OneDrive, SharePoint, Teams and Outlook.
              </p>
            </div>
          </div>
        </section>

        {/* Core Capabilities dark */}
        <section id="capabilities" className="pentities-docmgmt-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">Core capabilities</span>
            <h2 className="pentities-docmgmt-title">Built for large libraries and precise, runtime filtering</h2>
            <p className="pentities-docmgmt-lead">
              Searching vast content is hard. DocSearch lets you define custom refinement filters and act on
              results directly especially valuable in high-volume libraries.
            </p>

            <div className="pentities-docmgmt-block">
              <h3>1 · Custom refinement filters</h3>
              <p>
                Define your own refinement filters to cut through large libraries, setting runtime filters on
                metadata effortlessly.
              </p>
              <ul>
                <li><b>Any field type</b> filter on lookup, choice and managed-metadata fields.</li>
                <li><b>Auto-loaded values</b> each field loads all its possible values for precise filtering.</li>
                <li><b>Runtime control</b> change refinement fields whenever you need them.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>2 · Multiple lists &amp; libraries at once</h3>
              <p>
                Select several lists or libraries and work with them together no navigating each one separately.
              </p>
              <ul>
                <li><b>Combine sources</b> search across many lists and libraries simultaneously.</li>
                <li><b>Switch anytime</b> change the main list or library on the fly, even from a different SharePoint site you can access.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>3 · Permissions &amp; sharing from the results</h3>
              <p>
                Change permissions, share content and work with previous versions right from the search results without going to the source location.
              </p>
              <ul>
                <li><b>Manage access</b> set item and document permissions in place.</li>
                <li><b>Share instantly</b> create shareable links and send to the right people.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>4 · Metadata management</h3>
              <p>
                Open and edit metadata, review previous versions and jump to destination folders with ease.
              </p>
              <ul>
                <li><b>Edit in place</b> person fields, managed metadata, lookup fields and sensitivity labels.</li>
                <li><b>Full context</b> see every field of the selected library or list.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>5 · Universal search</h3>
              <p>
                Retrieve results from specific libraries and lists, or search across emails, chat messages and
                personal contacts and open the original location with one click.
              </p>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>6 · Version history</h3>
              <p>
                View and use previous versions of documents and list items, see exactly what changed in each
                version, and restore an earlier one when needed all without leaving the app.
              </p>
            </div>
          </div>
        </section>

        {/* Content Types icon card grid */}
        <section id="content-types" className="ds-ct-section">
          <div className="pentities-container">
            <span className="ds-ct-eyebrow">Search every content type</span>
            <h2 className="ds-ct-title">One search, results grouped by exactly what you're looking for</h2>
            <p className="ds-ct-lead">
              Depending on the result type, you get the right actions for viewing and managing it across eight
              dedicated views.
            </p>

            <div className="ds-ct-grid">
              <div className="ds-ct-card">
                <div className="ds-ct-icon"><SearchFilled /></div>
                <h3>All</h3>
                <p>Files, list items, folders, sites and image names in one unified view across every accessible source.</p>
              </div>
              <div className="ds-ct-card">
                <div className="ds-ct-icon"><DocumentFilled /></div>
                <h3>Files</h3>
                <p>Just files, with preview enabled open and work with documents directly, without leaving the app.</p>
              </div>
              <div className="ds-ct-card">
                <div className="ds-ct-icon"><FolderFilled /></div>
                <h3>Folders</h3>
                <p>Every matching folder, with one-click navigation to explore document structures.</p>
              </div>
              <div className="ds-ct-card">
                <div className="ds-ct-icon"><SettingsFilled /></div>
                <h3>Sites</h3>
                <p>All SharePoint sites matching your search, with instant navigation to the ones you can access.</p>
              </div>
              <div className="ds-ct-card">
                <div className="ds-ct-icon"><ImageFilled /></div>
                <h3>Images</h3>
                <p>Preview thumbnails and full images, then jump to their original locations.</p>
              </div>
              <div className="ds-ct-card">
                <div className="ds-ct-icon"><MailFilled /></div>
                <h3>Emails</h3>
                <p>Emails found across your mailbox, with navigation straight to the conversation in Outlook.</p>
              </div>
              <div className="ds-ct-card">
                <div className="ds-ct-icon"><ChatFilled /></div>
                <h3>Chat messages</h3>
                <p>Teams chat messages surfaced in search, so key decisions are one click away not buried in history.</p>
              </div>
              <div className="ds-ct-card">
                <div className="ds-ct-icon"><PeopleFilled /></div>
                <h3>Persons</h3>
                <p>Find people and groups across your organisation to connect with the right person fast.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Easy Installation dark */}
        <section id="installation" className="pentities-docmgmt-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">Easy installation</span>
            <h2 className="pentities-docmgmt-title">Up and running in three simple steps</h2>
            <p className="pentities-docmgmt-lead">
              Straightforward setup configured once by someone with app-install permissions, then ready for
              everyone.
            </p>

            <div className="pentities-docmgmt-block">
              <h3>1 · Select the list</h3>
              <p>
                Choose the list from the Team where the app is installed. You can change the main list or library
                later, even from a different site. This initial target is defined by personnel with the permissions
                to install apps.
              </p>
            </div>
            <div className="pentities-docmgmt-block">
              <h3>2 · Choose search fields</h3>
              <p>
                Select the fields to search on. Each automatically loads all its possible values lookup, choice or
                managed metadata becoming the refinement fields that filter your results efficiently.
              </p>
            </div>
            <div className="pentities-docmgmt-block">
              <h3>3 · Define editable fields</h3>
              <p>
                Select which fields should be editable (or choose "Select All"). After installation, users see the
                selected fields and can use the refinements to target searches within the chosen list or library.
              </p>
            </div>
          </div>
        </section>

        {/* Platform & ecosystem combined, card/chip appearance */}
        <section id="platform" className="ds-platform-section">
          <div className="pentities-container">
            <span className="ds-platform-eyebrow">The DocSearch platform</span>
            <h2 className="ds-platform-title">Everything it supports and the suite it belongs to</h2>

            <div className="ds-feature-grid">
              <div className="ds-feature-card">
                <h3>Supported environments</h3>
                <div className="ds-chips">
                  <span>Microsoft Teams</span>
                  <span>SharePoint</span>
                  <span>Microsoft 365 App</span>
                  <span>Outlook</span>
                  <span>Excel</span>
                  <span>Word</span>
                </div>
              </div>

              <div className="ds-feature-card">
                <h3>Search across</h3>
                <div className="ds-chips">
                  <span>SharePoint libraries</span>
                  <span>SharePoint lists</span>
                  <span>OneDrive</span>
                  <span>Mails</span>
                  <span>Contracts</span>
                  <span>Teams chat</span>
                </div>
              </div>

              <div className="ds-feature-card">
                <h3>Preview</h3>
                <div className="ds-chips">
                  <span>Documents</span>
                  <span>List items</span>
                  <span>Images</span>
                  <span>Mails</span>
                </div>
              </div>

              <div className="ds-feature-card ds-feature-card-wide">
                <h3>Core features</h3>
                <ul className="ds-check-list">
                  <li>Change search refinement &amp; editable fields, with administrator field locking</li>
                  <li>Change permissions, share items, download files and create shareable links</li>
                  <li>Navigate to the original location and see changed versions</li>
                  <li>Edit all non-read-only metadata term sets, persons and lookup fields</li>
                  <li>See every field of the selected list or library; change the selection anytime</li>
                  <li>Graphical representation of the results</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Get DocSearch dark AppSource section (same layout as Search Assistant, no video) */}
        <section className="demo-video-section">
          <div className="pentities-container">
            <div className="pentities-demo-layout">
              <div className="pentities-demo-content">
                <span className="pentities-demo-eyebrow">Get DocSearch</span>
                <h2 className="pentities-demo-title">
                  Install DocSearch straight from Microsoft AppSource
                </h2>
                <p>
                  DocSearch installs directly into your Microsoft 365 tenant no infrastructure to set up and
                  nothing to migrate. Add it once and your teams get unified, permission-aware search across
                  SharePoint, OneDrive, Outlook and Teams.
                </p>
                <p>
                  Everything runs inside your own tenant, using the permissions your people already have.
                </p>
              </div>
              <div className="pentities-demo-media">
                <div className="download-section">
                  <h3 className="download-title">Get DocSearch Now</h3>
                  <p className="download-description">
                    Download DocSearch directly from Microsoft AppSource and transform how your teams find content.
                  </p>
                  <a
                    href="https://marketplace.microsoft.com/en-us/product/WA200008044?tab=Overview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="appsource-link"
                  >
                    <img loading="lazy" decoding="async" src={msAppSource} alt="Get it from Microsoft AppSource" className="appsource-badge" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Part of the Meta-365 suite */}
        <section className="ds-platform-section ds-platform-suite">
          <div className="pentities-container">
            <span className="ds-platform-eyebrow">Explore the platform</span>
            <h2 className="ds-platform-title">DocSearch is one piece of a connected Microsoft 365 suite</h2>
            <div className="ds-suite">
              <h3 className="ds-suite-title">Part of the Meta‑365 suite</h3>
              <div className="pentities-modules-grid">
                <a href="/solutions/pentities" className="pentities-module-card">
                  <h3>pEntities Platform</h3>
                  <p>
                    Turn contracts, cases, HR records and projects into living workspaces inside Microsoft Teams.
                    Every record carries its documents, emails, tasks and approvals in one place, with a domain AI
                    agent that reads the data you already own and surfaces the risks and next steps that matter.
                  </p>
                  <span className="pentities-module-link">Explore →</span>
                </a>
                <a href="/solutions/pshareemail" className="pentities-module-card">
                  <h3>PShareEmail</h3>
                  <p>
                    Save Outlook emails straight to SharePoint, OneDrive or Teams and share them to the right
                    channels in a couple of clicks. Attachments, metadata and conversation context are preserved,
                    so important correspondence lands where your team can actually find and reuse it.
                  </p>
                  <span className="pentities-module-link">Explore →</span>
                </a>
                <a href="/solutions/searchassistant" className="pentities-module-card">
                  <h3>Search Assistant</h3>
                  <p>
                    Find and act on the right content faster, with an assistant that works in the flow of your
                    Microsoft 365 tools. Ask in natural language, get answers drawn from your own tenant, and jump
                    straight to the file, email or record you need right where you already work.
                  </p>
                  <span className="pentities-module-link">Explore →</span>
                </a>
                <a href="/solutions/automation" className="pentities-module-card">
                  <h3>Workflow Automation</h3>
                  <p>
                    No-code workflows that trigger on your own Microsoft 365 data and run themselves. Build
                    multi-step approvals, notifications and scheduled routines that react to changes across the
                    suite automating the repetitive work your teams do every day without writing any code.
                  </p>
                  <span className="pentities-module-link">Explore →</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <FaqSection path="/solutions/docsearch" />
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default DocSearchPage;
